import React, {
  useEffect,
  useState,
  useRef,
  RefObject,
  CSSProperties,
  useMemo,
  ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import DropdownItemAtom from "../item";
import styles from "./DropdownContainerAtom.module.css";
import DropdownTitleAtom from "../title";
import { useDevice } from "../../../hooks/useDevice";
import ActionSheet, { ActionSheetType } from "../../../components/action-sheet";

export type DropdownItemType = {
  label: string;
  value: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  itemKey?: number;
  state?: "default" | "disabled";
};

export type DropdownDataType = {
  title?: string;
  items: DropdownItemType[];
  extra?: string;
};

export type DropdownContainerType = {
  className?: string;
  containerSize?: "default" | "sm" | "md" | "lg";
  width?: CSSProperties["width"];

  /** Custom props */
  parent: RefObject<HTMLElement | null>;
  isOpen?: boolean;
  data?: DropdownDataType[];
  itemSize?: "sm" | "md";
  multiple?: boolean;
  selected?: DropdownItemType | DropdownItemType[];
  offsetTop?: number;
  textAlignment?: "left" | "center";
  customContent?: ReactNode;
  actionSheet?: ActionSheetType;
  onChange?: (item: DropdownItemType | DropdownItemType[]) => void;
  onActionSheetClose?: () => void;
};

const dropdownVariants = {
  hidden: {
    opacity: 0,
    transition: { duration: 0.2 },
  },
  visible: {
    opacity: 1,
    transition: { duration: 0.2 },
  },
};

export const DropdownContainerAtom: React.FC<DropdownContainerType> = ({
  className = "",
  containerSize = "default",
  width = "200px",
  parent,
  isOpen = false,
  data = [],
  itemSize = "sm",
  multiple = false,
  selected: selectedItems = [],
  offsetTop = 5,
  textAlignment = "left",
  onChange,
  onActionSheetClose,
  actionSheet,
  customContent,
}) => {
  const { isDesktop } = useDevice();
  const containerRef = useRef<HTMLDivElement>(null);
  const [topPosition, setTopPosition] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const [selected, setSelected] = useState<
    DropdownItemType | DropdownItemType[]
  >(selectedItems);

  let height: string | undefined;

  if (containerSize === "sm") {
    height = "220px";
  } else if (containerSize === "md") {
    height = "440px";
  } else if (containerSize === "lg") {
    height = "660px";
  } else if (containerSize === "default") {
    height = "unset";
  }

  const dropdownContainerStyle: CSSProperties = useMemo(() => {
    return {
      overflowY: isScrollable ? "scroll" : "hidden",
      maxHeight: height,
      width: width,
      top: `${topPosition}px`,
    };
  }, [height, topPosition]);

  useEffect(() => {
    setIsScrollable(containerSize !== "default");
  }, [containerSize]);

  useEffect(() => {
    if (parent.current && isOpen) {
      const elementHeight =
        parent.current.getBoundingClientRect().height + offsetTop;
      setTopPosition(elementHeight);
    }
  }, [parent, isOpen]);

  useEffect(() => {
    // Auto scroll on selected items
    if (!containerRef.current || !selected) return;

    const scrollToSelected = () => {
      // Skip if selected is null, undefined, or empty array
      if (!selected || (Array.isArray(selected) && selected.length === 0))
        return;

      const selectedItem = Array.isArray(selected) ? selected[0] : selected;
      const selectedElement = containerRef.current?.querySelector(
        `[data-value="${selectedItem.value}"]`
      );

      if (selectedElement && containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const elementOffset =
          selectedElement.getBoundingClientRect().top -
          containerRef.current.getBoundingClientRect().top +
          100;

        containerRef.current.scrollTop = elementOffset - containerHeight / 2;
      }
    };

    setTimeout(() => {
      scrollToSelected();
    }, 100);
  }, [isOpen]);

  const defaultRenderItem = ({
    label,
    value,
    iconLeft,
    iconRight,
  }: DropdownItemType) => {
    const isItemSelected = multiple
      ? Array.isArray(selected) && selected.some((item) => item.value === value)
      : !Array.isArray(selected) && selected?.value === value;

    return (
      <DropdownItemAtom
        size={itemSize}
        label={label}
        value={value}
        type={multiple ? "checkbox" : "text"}
        textAlignment={textAlignment}
        onClick={(e) => {
          if (multiple) {
            // For multiple selection, handle array of selected items
            const newSelected = Array.isArray(selected) ? [...selected] : [];
            const existingIndex = newSelected.findIndex(
              (item) => item.value === value
            );

            if (existingIndex >= 0) {
              newSelected.splice(existingIndex, 1);
            } else {
              newSelected.push({ label, value });
            }
            setSelected(newSelected);
            onChange?.(newSelected);
          } else {
            // For single selection, just set the selected item or deselect
            setSelected(e);
            onChange?.(e);
          }
        }}
        key={`item-${label}-${value}`}
        selected={isItemSelected}
        iconLeft={iconLeft}
        iconRight={iconRight}
      />
    );
  };

  // Create content for action sheet
  const renderDropdownContent = () => {
    if (customContent) {
      return customContent;
    }

    return (
      <div className={styles.listItems}>
        {data?.map(({ title, items }, key) => (
          <div className={styles.group1} key={key}>
            {title && <DropdownTitleAtom title={title} />}
            {items.length > 0 && items.map((item) => defaultRenderItem(item))}
            {title && <div className={styles.divider} />}
          </div>
        ))}
      </div>
    );
  };

  return (
    isOpen &&
    (data.length > 0 || customContent) && (
      <>
        {isDesktop ? (
          <AnimatePresence>
            <motion.div
              ref={containerRef}
              className={[
                styles.inputsDropdownListChe,
                "quill-ui-next-dropdown-container",
                className,
              ].join(" ")}
              style={dropdownContainerStyle}
              initial="hidden"
              animate={"visible"}
              exit="hidden"
              variants={dropdownVariants}
            >
              {renderDropdownContent()}
            </motion.div>
          </AnimatePresence>
        ) : (
          <ActionSheet
            {...actionSheet}
            show={isOpen}
            showHeader={false}
            showSearchBar={false}
            content={renderDropdownContent()}
            onClose={() => {
              onChange?.(
                Array.isArray(selected)
                  ? selected
                  : selected || { label: "", value: "" }
              );
              onActionSheetClose?.();
            }}
            type="bottom"
          />
        )}
      </>
    )
  );
};

export default DropdownContainerAtom;
